import { useCallback, useRef } from "react";

/**
 * implement a delayed close mechanism for a component, such as a menu or popup.
 * This hook provides functions to reset, schedule, and cancel the close action.
 *
 * @param delay The delay in milliseconds before the close action is executed.
 * @returns An object containing functions to reset, schedule, and cancel the close action.
 */
export function useDelayedClose(delay: number = 120) {
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const resetClose = useCallback(() => {
    clearTimeout(closeTimer.current);
    console.log("Resetting close timer");
  }, []);

  // supply a function to be called after the delay
  // when close actually happens
  const scheduleClose = useCallback(
    (closeFn: () => void) => {
      clearTimeout(closeTimer.current);
      closeTimer.current = setTimeout(() => {
        console.log("Closing menu after delay");
        closeFn();
      }, delay);
    },
    [delay],
  );

  const cancelClose = useCallback(() => {
    clearTimeout(closeTimer.current);
    console.log("Cancelled closing menu");
  }, []);

  return { resetClose, scheduleClose, cancelClose };
}
